import React, { FC, ReactNode, useEffect, useState } from 'react'

import { where } from 'firebase/firestore'
import { ChevronDownIcon } from 'lucide-react'

import { TeamEmpty } from '../../empty/TeamEmpty/TeamEmpty'
import { HorseTabItem } from '../HorseTabItem/HorseTabItem'
import { Button } from '../../../../../ui/button'

import {
  getHorseRequiredFields,
  hasInvalidRequiredField,
} from '../../../../../../helpers/requiredFields'
import { cn } from '../../../../../../lib/utils'

import FirestoreService from '../../../../../../services/firestoreService'
import HorseCompetitionPaperworkService from '../../../../../../services/firebase-service/horse-competition-paperwork/HorseCompetitionPaperworkService'
import HorsesService from '../../../../../../services/firebase-service/horses/HorsesService'

import { UserHorseMappingModel } from '../../../../../../models/user-horse-mapping/userHorseMapping.model'

import { IHorseCompetitorDocument } from '../../../../../../models/horse-competiton-paperwork/horse-competiton-paperwork-interface'
import { IUserHorseMappingInterface } from '../../../../../../models/user-horse-mapping/userHorseMapping.interface'
import { IHorseData } from '../../../../../../models/horse/horse.interface'
import { IEventReviewPublish } from '../../../../../../models/event-review-publish/event-review-publish.interface'
import { IRegistrationByDayInterface } from '../../../../../../models/registrations-by-day/registrationByDay.interface'

import { CONST } from '../../../../../../const/const'

const COLLECTIONS = CONST.DATA.FIRESTORE.LATEST.COLLECTIONS

export interface IHorsesWithPaperwork {
  horse: IHorseData
  paperwork: IHorseCompetitorDocument | null
}

interface HorsesTabProps {
  activeTab?: 'registrar' | 'overview'
  isCollapsible?: boolean
  className?: string
  registrationsByDay: IRegistrationByDayInterface[]
  publishedEvent: IEventReviewPublish | null
  onActionClick?: () => void
  actionIcon?: ReactNode
  isLoading?: boolean
}

export const HorsesTab: FC<HorsesTabProps> = ({
  activeTab,
  isCollapsible,
  className,
  registrationsByDay,
  publishedEvent,
  onActionClick,
  actionIcon,
  isLoading,
}) => {
  const [loading, setLoading] = useState(true)
  const [horsesWithPaperwork, setHorsesWithPaperwork] = useState<IHorsesWithPaperwork[]>([])
  const [filteredHorses, setFilteredHorses] = useState<IHorsesWithPaperwork[]>([])
  const [isOpen, setIsOpen] = useState(!isCollapsible)
  const [totalIssues, setTotalIssues] = useState(0)

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true)

        const horseIds: Set<string> = new Set()
        registrationsByDay.forEach((rider) => {
          rider.horsesSelected?.forEach((horse) => horseIds.add(horse.id))
        })
        const horsesInDb = await HorsesService.getHorsesByIds([...horseIds])

        if (!horsesInDb?.[0]) {
          setLoading(false)
          return
        }

        const horsesIds = horsesInDb.map((horse) => horse.id)
        const horsesHorseIds: string[] = []

        horsesInDb.forEach((horse) => {
          if (horse.horseId) horsesHorseIds.push(horse.horseId)
        })

        const userHorseMapping: IUserHorseMappingInterface[] = []
        const userHorseMappingSnaps = await FirestoreService.filterItems(
          COLLECTIONS.USER_HORSE_MAPPING.NAME,
          [where(COLLECTIONS.USER_HORSE_MAPPING.FIELDS.HORSE_ID.KEY, 'in', horsesIds)]
        )

        userHorseMappingSnaps.forEach((currDoc) => {
          const current = UserHorseMappingModel.fromFirestoreDoc(currDoc).toObject()
          userHorseMapping.push(current)
        })

        const horsesWithPaperworkUpdated: {
          horse: IHorseData
          paperwork: IHorseCompetitorDocument | null
        }[] = []

        const horsesCompetitionPaperwork: IHorseCompetitorDocument[] =
          await HorseCompetitionPaperworkService.getByHorseIds(horsesHorseIds)

        horsesHorseIds.forEach((horsesId) => {
          const currentHorse = horsesInDb.find((h) => h.horseId === horsesId)
          const currentPaperwork = horsesCompetitionPaperwork.find((h) => h.horseId === horsesId)

          const currentMapping = userHorseMapping?.find(
            (mapping) => mapping.horseId === currentHorse?.id
          )

          const isExist = horsesWithPaperworkUpdated.find(
            (horseCompetitionPaperwork) => horseCompetitionPaperwork.horse.id === currentHorse?.id
          )

          if (currentHorse?.id && !isExist) {
            horsesWithPaperworkUpdated.push({
              horse: currentHorse,
              paperwork:
                currentHorse?.id && currentPaperwork?.id === currentMapping?.id
                  ? currentPaperwork || null
                  : null,
            })
          }
        })

        setHorsesWithPaperwork(horsesWithPaperworkUpdated)
      } catch (error) {
        console.error(error, 'error')
      } finally {
        setLoading(false)
      }
    }
    if (!isLoading) getData().then()
  }, [isLoading, registrationsByDay])

  useEffect(() => {
    let totalCount = 0

    filteredHorses.forEach((filteredHorse) => {
      const eventRequiredHorseFields = publishedEvent?.EventRequiredFields.requiredHorseFields || []
      const fields = getHorseRequiredFields({
        horse: filteredHorse.horse,
        paperwork: filteredHorse.paperwork,
        eventRequiredHorseFields: eventRequiredHorseFields,
      })

      const fieldsFiltered = fields.filter((field) => !hasInvalidRequiredField(field))
      totalCount += fields.length - fieldsFiltered.length || 0
    })

    setTotalIssues(totalCount)
  }, [filteredHorses, publishedEvent])

  useEffect(() => {
    let filtered
    const eventRequiredHorseFields = publishedEvent?.EventRequiredFields.requiredHorseFields || []

    if (activeTab === 'overview') {
      filtered = horsesWithPaperwork.filter((horseWithPaperwork) => {
        const fields = getHorseRequiredFields({
          horse: horseWithPaperwork.horse,
          paperwork: horseWithPaperwork.paperwork,
          eventRequiredHorseFields,
        })

        const fieldsFiltered = fields.filter((field) => !hasInvalidRequiredField(field))

        return fields.length - fieldsFiltered.length > 0
      })
    } else {
      filtered = horsesWithPaperwork
    }
    setFilteredHorses(filtered)
  }, [publishedEvent?.EventRequiredFields.requiredHorseFields, horsesWithPaperwork, activeTab])

  if (loading || isLoading) return <TeamEmpty />

  return (
    <div
      className={cn(
        'rounded-lg border border-black/10 px-7 pt-3',
        !isOpen && 'pb-3',
        !filteredHorses?.[0] && 'py-3',
        className
      )}
    >
      <div className="flex gap-2 flex-wrap items-center justify-between">
        <div>
          <p className="text-base font-medium">Horses</p>
          <p className="text-xs text-black/50">
            {activeTab === 'overview'
              ? `${totalIssues > 0 ? `${totalIssues} issues` : 'No issues'}`
              : `${filteredHorses.length} horses`}
          </p>
        </div>
        <div className="text-right flex gap-3 items-center justify-end ml-auto">
          {activeTab === 'overview' ? (
            <Button
              variant="link"
              type="button"
              className="rounded-full p-0"
              onClick={onActionClick}
            >
              {actionIcon}
            </Button>
          ) : (
            totalIssues > 0 && (
              <div className="py-0.5 px-4 text-base font-normal rounded-full border border-transparent bg-SeabiscuitMainThemeColor/5 text-SeabiscuitMainThemeColor">
                {totalIssues} issue{totalIssues > 1 ? 's' : ''}
              </div>
            )
          )}
          {isCollapsible && (
            <Button
              variant="link"
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className={cn(
                'rounded-full transition-all duration-200 w-[30px] h-[30px] flex items-center justify-center shrink-0',
                isOpen
                  ? 'text-SeabiscuitMainThemeColor bg-SeabiscuitMainThemeColor/5 rotate-180'
                  : 'text-black bg-black/5'
              )}
            >
              <ChevronDownIcon className="size-4" />
            </Button>
          )}
        </div>
      </div>
      {isOpen && (
        <div className={cn((!isCollapsible || isOpen) && filteredHorses?.[0] && 'mt-3')}>
          {filteredHorses.map((item, index) => (
            <HorseTabItem
              key={index}
              index={index}
              item={item}
              activeTab={activeTab}
              requiredHorseFields={publishedEvent?.EventRequiredFields.requiredHorseFields}
              horsesWithPaperwork={horsesWithPaperwork}
              setHorsesWithPaperwork={setHorsesWithPaperwork}
            />
          ))}
        </div>
      )}
    </div>
  )
}
